import { type LiveHoldingsResult, fetchLiveHoldings } from "@/data/etf-api";
import type { FundDetail, Holding } from "@/data/fund-detail-types";
import { type Top10HoldingsPayload, getLeveragedTop10 } from "@/data/lev2x-top10-holdings";
import { useEtfFundList } from "@/hooks/use-etf-fund-list";
import type { useLeveragedHoldings } from "@/hooks/use-leveraged-holdings";
import { downloadCsv, rowsToCsv } from "@/lib/csv-download";
import { fundDetailPath } from "@/lib/fund-paths";
/**
 * Holdings table for leveraged funds (FDRX). Ported from the dane
 * etf-website `components/leveraged-holdings-table.tsx`. In the static
 * replica the swap-collateral rows + underlying basket come from the live
 * feed (absent), so the table renders its swap callout + "Holdings
 * unavailable." state.
 */
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  FundHoldingsSidePanel,
  type FundHoldingsTabData,
  type FundHoldingsTabRow,
} from "./fund-holdings-side-panel";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatAsOf(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(`${iso.length <= 10 ? iso : iso.split("T")[0]!}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatPct(v: number): string {
  return `${v >= 0 ? "" : "−"}${Math.abs(v).toFixed(2)}%`;
}

type TypePill = "TRS" | "T-BILL" | "CASH";

// Live ingested security names vary ("CORGI ETF TR SWAP CS", "First American
// Government Obligations Fund 12/01/2031", "Cash & Other", …), so classify by
// pattern rather than exact string match.
const SWAP_RE = /(?:total[\s-]*return|tr)[\s-]*swap|\bt\.?r\.?s\b|\bswap\b/i;
const COLLATERAL_RE =
  /government\s+obligation|gov.?t\s+obligation|money\s*market|treasur|t-?bill|first\s+american/i;
const CASH_RE = /\bcash\b/i;

function describeRow(
  rowName: string,
  underlyingTicker?: string,
): { description: string; pill: TypePill | null } {
  if (SWAP_RE.test(rowName)) {
    return {
      description: `Total Return Swap on ${underlyingTicker ?? "Underlying ETF"}`,
      pill: "TRS",
    };
  }
  if (COLLATERAL_RE.test(rowName)) {
    return { description: "U.S. Treasury Money Market Fund", pill: "T-BILL" };
  }
  if (CASH_RE.test(rowName)) {
    return { description: "Cash, accruals & other net assets", pill: "CASH" };
  }
  return { description: "", pill: null };
}

const TYPE_PILL_CLASS: Record<TypePill, string> = {
  TRS: "bg-[rgba(255,85,0,0.15)] text-[var(--etf-orange)] border-[rgba(255,85,0,0.3)]",
  "T-BILL": "bg-[rgba(0,178,158,0.12)] text-[#00b29e] border-[rgba(0,178,158,0.3)]",
  CASH: "bg-white/[0.06] text-[var(--etf-white3)] border-[var(--etf-border)]",
};

export function LeveragedHoldingsTable({
  fund,
  fallbackDateLabel,
  leveraged,
  sectorBreakdownSlot,
}: {
  fund: FundDetail;
  fallbackDateLabel?: string;
  leveraged: ReturnType<typeof useLeveragedHoldings>;
  sectorBreakdownSlot?: ReactNode;
}) {
  const ticker = fund.ticker;
  const underlyingTicker = fund.underlyingEtfTicker;
  const underlyingName = fund.underlyingEtfName ?? underlyingTicker ?? "Underlying ETF";

  const { funds: liveFunds } = useEtfFundList();
  const liveUnderlyingFund = underlyingTicker
    ? liveFunds.find((f) => f.ticker === underlyingTicker)
    : undefined;
  const underlyingCtaHref = liveUnderlyingFund
    ? fundDetailPath(liveUnderlyingFund.slug)
    : undefined;

  // Prefer the fund's own `underlyingEtfName` over the live underlying fund's
  // full_name. They normally agree, but when the underlying is itself a Corgi
  // fund whose catalog name uses a different shorthand (e.g. DMAG → CMAG, whose
  // full_name is "Corgi Magnificent Seven ETF" while DMAG labels its underlying
  // "Corgi Mag 7 ETF"), the fund's own field is authoritative for how it refers
  // to its underlying — matching the hero/banner/objective copy.
  const underlyingDisplayLabel = fund.underlyingEtfName
    ? `the ${fund.underlyingEtfName}`
    : liveUnderlyingFund?.full_name
      ? `the ${liveUnderlyingFund.full_name}`
      : underlyingTicker
        ? underlyingTicker
        : "the underlying";

  const { rows, asOf, loading } = leveraged;

  const [underlying, setUnderlying] = useState<LiveHoldingsResult | null>(null);

  const staticTop10: Top10HoldingsPayload | undefined = getLeveragedTop10(underlyingTicker);

  useEffect(() => {
    if (!underlyingTicker || !liveUnderlyingFund) return;
    if (staticTop10) return;
    let cancelled = false;
    fetchLiveHoldings(underlyingTicker).then((result) => {
      if (cancelled) return;
      setUnderlying(result);
    });
    return () => {
      cancelled = true;
    };
  }, [underlyingTicker, liveUnderlyingFund, staticTop10]);

  const [panelOpen, setPanelOpen] = useState(false);
  const expandBtnRef = useRef<HTMLButtonElement>(null);
  const closePanel = useCallback(() => {
    setPanelOpen(false);
    requestAnimationFrame(() => expandBtnRef.current?.focus());
  }, []);

  const handleDownload = useCallback(() => {
    if (!rows || rows.length === 0) return;
    const slug = ticker.toLowerCase();
    const dateForFile = (asOf || "").replace(/[^A-Za-z0-9]+/g, "-");
    const filename = `${slug}-holdings${dateForFile ? `-${dateForFile}` : ""}.csv`;
    const csv = rowsToCsv(rows, [
      { header: "Name", value: (r) => r.name },
      { header: "Type", value: (r) => r.typeLabel },
      { header: "Weight (%)", value: (r) => r.weightPct.toFixed(2) },
      { header: "Market Value", value: (r) => r.marketValue.toFixed(2) },
    ]);
    downloadCsv(filename, csv);
  }, [rows, ticker, asOf]);

  const dateLabel = asOf ? formatAsOf(asOf) : fallbackDateLabel || "—";

  const staticTopTen: Holding[] =
    staticTop10?.entries.map((e) => ({
      ticker: e.ticker ?? "",
      name: e.name,
      sector: "",
      pct: e.weightPct,
      marketValue: "",
      ...(e.cusip ? { cusip: e.cusip } : {}),
    })) ?? [];

  const liveHoldings = underlying?.holdings ?? [];
  const liveCount = underlying?.holdingsCount ?? 0;
  const underlyingHoldings = staticTop10 ? staticTopTen : liveHoldings;
  const underlyingCount = staticTop10 ? staticTopTen.length : liveCount;
  const underlyingTopTen = underlyingHoldings.slice(0, 10);
  const underlyingMaxPct = underlyingTopTen[0]?.pct ?? 1;
  const underlyingDateLabel = staticTop10?.asOf
    ? formatAsOf(staticTop10.asOf)
    : underlying?.asOfDate
      ? formatAsOf(underlying.asOfDate)
      : dateLabel;

  const fundHoldingsTab: FundHoldingsTabData | undefined = (() => {
    if (!underlyingTicker) return undefined;
    const tabRows: FundHoldingsTabRow[] = (rows ?? []).map((r) => {
      const { description, pill } = describeRow(r.name, underlyingTicker);
      return {
        name: r.name,
        description,
        pill,
        weightPct: r.weightPct,
        highlight: r.highlight,
      };
    });
    return {
      rows: tabRows,
      callout: {
        label: "Swap-Based Structure",
        body: `${ticker} achieves 2x daily exposure via TRS (Total Return Swaps). Direct holdings are collateral only. Underlying equity exposure is via ${underlyingTicker}.`,
      },
      asOfLabel: `As of ${dateLabel} · Weights are approximate`,
      ctaHref: underlyingCtaHref,
      ctaLabel: underlyingCtaHref ? `View ${underlyingTicker} Page →` : undefined,
      loadingText: loading ? "Loading holdings…" : "Holdings unavailable.",
    };
  })();

  return (
    <div className="border-b border-white/[0.13]" id="sec-holdings">
      <div className="flex items-center justify-between gap-3 px-10 py-6 border-b border-white/[0.13] max-md:px-4 max-md:py-4 max-md:flex-col max-md:items-start">
        <div className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3 max-md:text-[20px]">
          <span className="inline-block w-[3px] h-[20px] bg-[var(--etf-orange)]" />
          Holdings
        </div>
        <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.08em] max-md:text-[11px]">
          <span className="text-[var(--etf-orange)] mr-1.5">Swap-Based &middot;</span>
          As of {dateLabel}
        </div>
      </div>

      <div className="px-10 py-6 border-b border-[var(--etf-border2)] max-md:px-4">
        <div className="border-l-[3px] border-l-[var(--etf-orange)] bg-[rgba(255,85,0,0.03)] py-5 px-6">
          <div className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.18em] uppercase text-[var(--etf-orange)] mb-2.5">
            Swap-Based Exposure
          </div>
          <p className="font-[family-name:var(--font-geist)] text-[13px] font-light text-[var(--etf-white2)] leading-[1.7] m-0">
            {fund.leveragedSubtype === "single-stock" && fund.underlyingIssuer ? (
              <>
                This fund uses swap agreements and other financial instruments to achieve 2x daily
                performance of the Underlying Security ({fund.underlyingIssuer} common stock). The
                Fund obtains exposure to {fund.underlyingTicker} via total return swaps rather than
                holding shares directly.
              </>
            ) : (
              <>
                This fund uses swap agreements and other financial instruments to achieve 2x daily
                performance of {underlyingDisplayLabel}. The Fund does not directly hold stocks
                {fund.underlyingEtfName && underlyingTicker
                  ? `. Underlying holdings of ${underlyingTicker} are shown below`
                  : ""}
                .
              </>
            )}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[520px]">
          <thead>
            <tr>
              <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] text-left pl-10 pr-3 py-3 border-b border-[var(--etf-border2)] font-normal max-md:pl-4">
                Instrument
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] text-right px-4 py-3 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap">
                Type
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white3)] text-right pr-10 pl-4 py-3 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:pr-4">
                Weight
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={3}
                  className="px-10 py-8 text-center text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] text-[12px] italic"
                >
                  Loading holdings…
                </td>
              </tr>
            )}
            {!loading && (!rows || rows.length === 0) && (
              <tr>
                <td
                  colSpan={3}
                  className="px-10 py-8 text-center text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] text-[12px] italic"
                >
                  Holdings unavailable.
                </td>
              </tr>
            )}
            {!loading &&
              rows &&
              rows.map((row) => {
                const { description, pill } = describeRow(row.name);
                return (
                  <tr
                    key={row.name}
                    className="transition-[background] duration-[120ms] hover:bg-white/[0.02]"
                    style={row.highlight ? { background: "rgba(255,85,0,0.03)" } : undefined}
                  >
                    <td className="pl-10 pr-3 py-3.5 border-b border-[var(--etf-border2)] max-md:pl-4">
                      <div
                        className="font-[family-name:var(--font-instrument)] text-[13px] font-semibold tracking-[0.06em] mb-[3px]"
                        style={{ color: row.highlight ? "var(--etf-orange)" : "var(--etf-white)" }}
                      >
                        {row.name}
                      </div>
                      {description && (
                        <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)]">
                          {description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 border-b border-[var(--etf-border2)] text-right">
                      {pill && (
                        <span
                          className={`font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.04em] px-2 py-[2px] border ${TYPE_PILL_CLASS[pill]}`}
                        >
                          {pill}
                        </span>
                      )}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] font-medium text-[var(--etf-white)] text-right pr-10 pl-4 py-3.5 border-b border-[var(--etf-border2)] [font-variant-numeric:tabular-nums] max-md:pr-4">
                      {formatPct(row.weightPct)}
                    </td>
                  </tr>
                );
              })}
            {!loading && rows && rows.length > 0 && (
              <tr className="bg-[var(--etf-black)]">
                <td className="pl-10 pr-3 py-3.5 border-t border-[var(--etf-border)] max-md:pl-4">
                  <div className="font-[family-name:var(--font-instrument)] text-[13px] font-bold text-[var(--etf-white)] tracking-[0.06em]">
                    TOTAL
                  </div>
                </td>
                <td className="px-4 py-3.5 border-t border-[var(--etf-border)]" />
                <td className="font-[family-name:var(--font-instrument)] text-[13px] font-bold text-[var(--etf-white)] text-right pr-10 pl-4 py-3.5 border-t border-[var(--etf-border)] [font-variant-numeric:tabular-nums] max-md:pr-4">
                  {formatPct(rows.reduce((s, r) => s + r.weightPct, 0))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {sectorBreakdownSlot}

      {underlyingTicker && (liveUnderlyingFund || staticTop10) && (
        <>
          <div className="px-10 max-md:px-4 pt-6 pb-3 border-t border-[var(--etf-border)]">
            <div className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.16em] uppercase text-[var(--etf-orange)]">
              Underlying ETF Holdings
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] mt-1">
              {staticTop10
                ? staticTop10.entries.length === 0
                  ? `${underlyingName} · no equity holdings`
                  : `Top ${Math.min(underlyingTopTen.length, 10)} of ${underlyingCount} · ${underlyingName}`
                : underlying
                  ? `Top ${Math.min(underlyingTopTen.length, 10)} of ${underlyingCount} · ${underlyingName}`
                  : `Loading ${underlyingName} holdings…`}
            </div>
          </div>

          <div className="grid grid-cols-[32px_1fr_96px_116px_64px_96px] max-md:grid-cols-[28px_minmax(0,1fr)_64px] gap-4 max-md:gap-2.5 items-center px-10 max-md:px-4 py-4 border-b border-white/[0.13] opacity-50 pointer-events-none">
            <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)]">
              #
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.1em] text-[var(--etf-white3)] uppercase">
              Company
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.1em] text-[var(--etf-white3)] uppercase pl-4 max-md:hidden">
              Weight
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.1em] text-[var(--etf-white3)] uppercase whitespace-nowrap max-md:hidden">
              Market Value
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.1em] text-[var(--etf-white3)] uppercase text-right pr-4 whitespace-nowrap">
              Wgt %
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.1em] text-[var(--etf-white3)] uppercase max-md:hidden">
              CUSIP
            </span>
          </div>

          {staticTop10 && staticTop10.entries.length === 0 && (
            <div className="px-10 max-md:px-4 py-6 text-center font-[family-name:var(--font-instrument)] text-[12px] italic text-[var(--etf-white3)]">
              No equity holdings — {underlyingName} is a physical-commodity / commodity-futures
              fund.
            </div>
          )}
          {!staticTop10 && !underlying && (
            <div className="px-10 max-md:px-4 py-6 text-center font-[family-name:var(--font-instrument)] text-[12px] italic text-[var(--etf-white3)]">
              Loading {underlyingName} holdings…
            </div>
          )}
          {!staticTop10 && underlying && underlyingTopTen.length === 0 && (
            <div className="px-10 max-md:px-4 py-6 text-center font-[family-name:var(--font-instrument)] text-[12px] italic text-[var(--etf-white3)]">
              Holdings unavailable.
            </div>
          )}
          {underlyingTopTen.map((h, i) => (
            <UnderlyingHoldingRow
              key={`${h.ticker || h.name}-${i}`}
              holding={h}
              index={i}
              maxPct={underlyingMaxPct}
            />
          ))}
        </>
      )}

      <div className="flex items-center justify-between gap-3 px-10 py-4 border-t border-white/[0.13] max-md:px-4 flex-wrap">
        <span className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] max-md:text-[12px]">
          {underlyingTicker ? (
            <>
              {`Swap-based\u00A0·\u00A0Underlying: ${underlyingTicker}\u00A0·\u00A0${staticTop10 ? "—" : underlyingCount || "—"} total holdings (subject to change)`}
            </>
          ) : (
            "Swap-based holdings"
          )}
        </span>
        <div className="flex items-center gap-4 max-md:gap-3 flex-wrap">
          <button
            type="button"
            className="inline-flex items-center gap-[5px] py-[5px] pr-[13px] pl-[11px] border-[1.5px] border-[var(--etf-orange)] rounded-full text-[var(--etf-orange)] font-[family-name:var(--font-instrument)] text-[11px] cursor-pointer bg-transparent whitespace-nowrap tracking-[0.04em] uppercase transition-colors duration-[250ms] relative overflow-hidden disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[rgba(255,85,0,0.06)]"
            onClick={handleDownload}
            disabled={!rows || rows.length === 0}
            aria-label="Download swap-based holdings as CSV"
          >
            <span className="relative z-[1]" aria-hidden="true">
              &#8595;
            </span>
            <span className="relative z-[1]">Download Holdings</span>
          </button>
          {underlyingTicker && underlyingCount > 10 && (
            <button
              type="button"
              ref={expandBtnRef}
              onClick={() => setPanelOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={panelOpen}
              className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] hover:opacity-80 transition-colors cursor-pointer bg-transparent border-0"
            >
              View All Holdings &rarr;
            </button>
          )}
        </div>
      </div>

      {underlyingTicker && (
        <FundHoldingsSidePanel
          open={panelOpen}
          onClose={closePanel}
          holdings={underlyingHoldings}
          holdingsCount={underlyingCount}
          dateLabel={underlyingDateLabel}
          ticker={ticker}
          fundName={fund.name}
          fundHoldingsTab={fundHoldingsTab}
          initialTab="underlying"
          underlyingTabSubtitle={
            <>
              Underlying: <span className="text-[var(--etf-orange)]">{underlyingTicker}</span>{" "}
              &mdash; {underlyingName}
            </>
          }
        />
      )}
    </div>
  );
}

function UnderlyingHoldingRow({
  holding,
  index,
  maxPct,
}: {
  holding: Holding;
  index: number;
  maxPct: number;
}) {
  const barWidth = maxPct > 0 ? ((holding.pct ?? 0) / maxPct) * 100 : 0;
  return (
    <div className="grid grid-cols-[32px_1fr_96px_116px_64px_96px] max-md:grid-cols-[28px_minmax(0,1fr)_64px] gap-4 max-md:gap-2.5 items-center px-10 max-md:px-4 py-4 max-md:py-3.5 border-b border-white/[0.08] hover:bg-white/[0.02] transition-colors">
      <span className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] tabular-nums">
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="min-w-0">
        <div className="font-[family-name:var(--font-instrument)] text-sm font-medium text-[var(--etf-orange)] mb-0.5">
          {holding.ticker}
        </div>
        <div className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] max-md:text-[13px] max-md:truncate">
          {holding.name}
        </div>
      </div>
      <div className="pl-4 pr-4 max-md:hidden">
        <div className="h-1 rounded bg-white/[0.05] overflow-hidden">
          <div
            className="h-full bg-[linear-gradient(90deg,var(--etf-orange),color-mix(in_srgb,var(--etf-orange)_70%,white))] rounded-sm shadow-[0_0_8px_rgba(255,85,0,0.4)]"
            style={{ width: `${Math.max(0, Math.min(100, barWidth))}%` }}
          />
        </div>
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] tabular-nums pl-10 max-md:hidden">
        {holding.marketValue}
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] text-right tabular-nums pr-4">
        {holding.pct == null ? "—" : `${holding.pct.toFixed(2)}%`}
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tabular-nums max-md:hidden">
        {holding.cusip || "—"}
      </div>
    </div>
  );
}
